// Traduce la pagina mentre Vue la disegna: ogni testo e gli attributi che si
// leggono (placeholder, title, alt, aria-label) passano dal dizionario della
// lingua scelta. Il codice e i template restano in italiano, e un testo nuovo
// in un componente nuovo si traduce appena il dizionario lo conosce.
//
// Come lo fanno i servizi di traduzione dei siti: un MutationObserver vede
// ogni testo che Vue scrive e lo sostituisce prima che il browser disegni
// (i microtask girano prima del rendering), quindi l'italiano non lampeggia.
// Per ogni nodo resta l'originale italiano: quando Vue lo cambia si traduce
// il testo nuovo, e tornando all'italiano si rimette l'originale.
//
// Fuori dalla traduzione: ciò che sta sotto translate="no" o .notranslate
// (i nomi delle lingue nel selettore, i documenti legali), script e stili,
// i campi in cui si scrive.

const ATTRIBUTES = ['placeholder', 'title', 'alt', 'aria-label'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE', 'IFRAME', 'TEMPLATE']);

const textState = new WeakMap(); // Text -> { source, shown }
const attrState = new WeakMap(); // Element -> Map(attr -> { source, shown })

function isExcluded(node) {
  let el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.getAttribute && el.getAttribute('translate') === 'no') return true;
    if (el.classList && el.classList.contains('notranslate')) return true;
    if (el.isContentEditable) return true;
    el = el.parentElement;
  }
  return false;
}

function translateText(node, translate) {
  const value = node.nodeValue;
  if (!value || !value.trim()) return;
  const state = textState.get(node);
  // Il testo che abbiamo scritto noi: niente da fare.
  if (state && value === state.shown) return;
  if (isExcluded(node)) return;
  const shown = translate(value);
  textState.set(node, { source: value, shown });
  if (shown !== value) node.nodeValue = shown;
}

function translateAttributes(el, translate) {
  if (!el.getAttribute) return;
  for (const name of ATTRIBUTES) {
    const value = el.getAttribute(name);
    if (!value || !value.trim()) continue;
    let map = attrState.get(el);
    const state = map && map.get(name);
    if (state && value === state.shown) continue;
    if (isExcluded(el)) return;
    const shown = translate(value);
    if (!map) {
      map = new Map();
      attrState.set(el, map);
    }
    map.set(name, { source: value, shown });
    if (shown !== value) el.setAttribute(name, shown);
  }
}

function walk(root, visitText, visitElement) {
  if (!root) return;
  if (root.nodeType === Node.TEXT_NODE) {
    visitText(root);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE) {
    if (SKIP_TAGS.has(root.tagName)) return;
    visitElement(root);
  }
  const doc = root.ownerDocument || root;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      if (n.nodeType === Node.ELEMENT_NODE && SKIP_TAGS.has(n.tagName)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n = walker.nextNode();
  while (n) {
    if (n.nodeType === Node.TEXT_NODE) visitText(n);
    else visitElement(n);
    n = walker.nextNode();
  }
}

export function createDomTranslator({ root, titleElement } = {}) {
  let observer = null;
  let translate = null;

  function translateTree(node) {
    walk(node, (t) => translateText(t, translate), (e) => translateAttributes(e, translate));
  }

  function onMutations(records) {
    if (!translate) return;
    for (const r of records) {
      if (r.type === 'characterData') translateText(r.target, translate);
      else if (r.type === 'attributes') translateAttributes(r.target, translate);
      else r.addedNodes.forEach((n) => translateTree(n));
    }
  }

  return {
    // Traduce tutto ciò che c'è e quello che arriverà, con translateFn.
    start(translateFn) {
      translate = translateFn;
      translateTree(root);
      if (titleElement) translateTree(titleElement);
      if (!observer && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(onMutations);
        const options = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRIBUTES };
        observer.observe(root, options);
        if (titleElement) observer.observe(titleElement, options);
      }
    },
    // Smette di tradurre e rimette i testi italiani dove li aveva cambiati.
    stop() {
      if (observer) {
        observer.takeRecords();
        observer.disconnect();
        observer = null;
      }
      translate = null;
      const restore = (node) =>
        walk(
          node,
          (t) => {
            const s = textState.get(t);
            if (s && t.nodeValue === s.shown) t.nodeValue = s.source;
            textState.delete(t);
          },
          (e) => {
            const map = attrState.get(e);
            if (!map) return;
            map.forEach((s, name) => {
              if (e.getAttribute(name) === s.shown) e.setAttribute(name, s.source);
            });
            attrState.delete(e);
          },
        );
      restore(root);
      if (titleElement) restore(titleElement);
    },
    // Per i test: applica subito le mutazioni in coda.
    flush() {
      if (observer) onMutations(observer.takeRecords());
    },
  };
}
